import { parseUrl } from '@/features/shared/utils/queryString'
import { type RumEvent, type RumEventDomainContext } from '@datadog/browser-rum'
import { type LogsEvent } from '@datadog/browser-logs'
import { redactSensitiveData } from '@/lib/datadog/redaction'
import { classifyError, shouldReportError } from '@/lib/datadog/errorGrouping'
import { type LogMeta } from '@/features/shared/utils/logger/types'
declare module '@datadog/browser-logs' {
  interface LogsEvent {
    metadata?: LogMeta
  }
}

/** returns true if browser is supported by Segway */
export function isSupportedBrowser() {
  // Instead of checking user agents for supported browsers, we can check for
  // support for individual features since user agents can be spoofed.
  // This checks that the CSS :has() selector is supported which ensures we
  // are >= Safari 15.4.
  return CSS.supports('selector(:has(a))')
}

const FILTERED_ORIGINS = ['network', 'console']

function isFilteredOrigin(origin: string): boolean {
  return FILTERED_ORIGINS.includes(origin)
}

export function extractCmsSlug(event: RumEvent, ctx: RumEventDomainContext) {
  if (
    event.type === 'resource' &&
    event.resource.url.includes('cms/v1/page-content') &&
    event.resource.status_code === 200 &&
    'xhr' in ctx
  ) {
    let cmsSlug = 'unknown'
    try {
      const parsedBody: { slug: string } = JSON.parse(ctx.xhr.response)
      cmsSlug = parsedBody.slug
    } catch (error) {
      // do nothing
    }

    const context = event.context ?? {}
    context.cmsSlug = cmsSlug
    event.context = context
  }
}
export function extractGraphQLOperationNameAndErrors(event: RumEvent, ctx: RumEventDomainContext) {
  if (event.type === 'resource' && event.resource.url.includes('graphql')) {
    const { query } = parseUrl(event.resource.url)
    const context = event.context ?? {}
    event.context = context
    if (query.operationName) {
      context.operationName = query.operationName
    }

    if ('xhr' in ctx) {
      try {
        const response = JSON.parse(ctx.xhr.response)
        // if no data or data is empty object, this is a fatal error
        if (!response.data || Object.keys(response.data).length === 0) {
          context.graphQlErrorType = 'fatal'
        } else if (response.errors) {
          // if the errors array is present, this is a partial, non fatal error
          context.graphQlErrorType = 'partial'
        }
      } catch (error) {
        // do nothing
      }
    }
  }
}
export function processLogEvent(log: LogsEvent): boolean {
  if (!isSupportedBrowser()) {
    return false
  }

  // Filter out network and console errors — already captured by RUM
  if (isFilteredOrigin(log.origin)) {
    return false
  }

  // Apply the same ignore logic as datadogErrorHandler for forwarded errors
  if (!shouldReportError(log.message, log.error?.stack)) {
    return false
  }

  // Only classify if the error has not already been classified (i.e. forwarded errors, errors not logged by application code)
  if (log.metadata?.errorClassification === undefined) {
    const classification = classifyError(log.message)
    log.metadata = {
      ...log.metadata,
      errorClassification: classification.type,
      ...classification.tags,
    }
  }

  // Recursively redact all PII in the entire log object
  Object.assign(log, redactSensitiveData(log))
  return true
}
